/* iceberg-ui */
import { Button, Flex, IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Check, WatchLater } from '@mui/icons-material';
import {
    Box,
    Checkbox,
    Chip,
    FormControlLabel,
    Grid,
    Paper,
    TableCell,
    Typography
} from '@mui/material';

/*  Shared  */
import { AddUser } from 'application/Shared/Components/Buttons/AddUser';
import { ManagedTable } from 'application/Shared/Components/Table';
import { DarkTooltip, TooltipQ } from 'application/Shared/Components/Tooltips';
import { Notify, Response, SessionHelper } from 'application/Shared/Helpers';
import { Confirm } from 'application/Shared/Modals';
import { DataAPI, Requests, WelcomeEmail } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';
import i18n from 'i18n';

/*  Objects  */
import { User, UserIconAndName } from 'objects/User';

/*  React  */
import React, { Component } from 'react';
import { Trans, withTranslation } from 'react-i18next';
import { withRouter } from 'router/withRouter';

import { DisableButton } from './DisableButton';
import { EditUserOrInvitePermission } from './EditUserOrInvitePermission';

/*  Constants  */
const { getData, hasData } = Response;

const headers = [
    {
        renderHeader: i18n.t('Common.user'),
        sortableKey: 'name',
        width: '30%'
    },
    {
        renderHeader: i18n.t('Common.email'),
        sortableKey: 'email',
        width: '35%'
    },

    {
        renderHeader: i18n.t('Common.permission'),
        width: '15%'
    },
    {
        renderHeader: i18n.t('Common.invitation_status'),
        width: '15%'
    },
    {
        align: 'right',
        renderHeader: i18n.t('Common.actions'),
        width: '5%'
    }
];

/*  State  */
const state = () => ({
    combinedUserRows: [],
    currToggles: null,
    disabling: false,
    enabling: false,
    inputToggles: null,
    loaded: false,
    permissions: [],
    rerender: false,
    resending: false,
    revoking: false,
    tableLoaded: false
});

const isInvitePending = (invite) => invite?.accepted === false;

const searchFields = [
    'user.firstName',
    'user.lastName',
    'user.email',
    'user.title',
    'invite.email'
];

class Users extends Component {
    constructor(props) {
        super(props);

        this.state = this.initToggles();

        /* requests */
        this.requests = new Requests();
        this.WelcomeEmail = new WelcomeEmail();
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unload();
    }

    load = () => {
        this.loadPermissions();
        this.loadUsers();

        if (!this.unmount) {
            this.setState({ loaded: true });
        }
    };

    rerender = () => {
        if (this.unmount) {
            return;
        }

        this.setState({ rerender: true }, () => {
            this.setState({ rerender: false });
            this.loadUsers();
        });
    };

    unload = () => {
        this.unmount = true;
        this.requests.cancel();
        this.WelcomeEmail.cancel();
    };

    enable = async (p) => {
        const { t, match } = this.props;
        const { clientId } = match.params;
        const { id } = p;

        /* params */
        const params = { disabled: false };
        if (clientId) {
            params.platformTenantId = clientId;
        }

        try {
            await axios.put(`/user/${id}`, params);
            this.rerender();
            Notify.s(t('User has been enabled.'));
        } catch (e) {
            debug.e('Unable to enable User', e);
            Notify.e(t('Common.error'));
        }
    };

    disable = async (p) => {
        const { t, match } = this.props;
        const { clientId } = match.params;
        const { id } = p;

        /* params */
        const params = { disabled: true };
        if (clientId) {
            params.platformTenantId = clientId;
        }

        try {
            await axios.put(`/user/${id}`, params);
            this.setState({ disabling: false });
            this.rerender();
            Notify.i(t('User has been disabled.'));
        } catch (e) {
            debug.e('Unable to disable User', e);
            Notify.e(t('Common.error'));
        }
    };

    handleRows = (rows) => {
        const { tableLoaded } = this.state;

        if (!rows || tableLoaded) {
            return;
        }

        this.setState({ tableLoaded: true });
    };

    loadPermissions = async () => {
        const { match } = this.props;
        const { clientId } = match.params;

        const params = { size: -1 };
        if (clientId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }

        try {
            const response = await axios.get('/permission', {
                cancelToken: this.requests.token(),
                params
            });
            if (!Response.hasData(response)) {
                debug.e('No Permission Found', response);
                return;
            }

            const permissions = Response.getData(response);
            this.setState({ permissions });
        } catch (e) {
            debug.e('Unable to load permissions', e);
        }
    };

    loadUsers = async () => {
        const {
            currToggles: { excludeDisabled }
        } = this.state;

        // Load invitations
        let invitationRows = [];

        try {
            const response = await axios.get('/invitation', {
                params: { size: -1 }
            });
            if (hasData(response)) {
                invitationRows = getData(response);
            }
        } catch (e) {
            return;
        }

        // Load users
        const dataAPI = new DataAPI({ route: '/user' });
        const params = {
            firstName: '*',
            hasEmail: true,
            ignoreDisabled: !excludeDisabled.enabled,
            size: -1
        };
        dataAPI.addParameters(params);

        const userRows = await dataAPI.getResults();

        const restructuredUserRows = userRows.map((u) => ({
            invite: null,
            user: u
        }));

        // Merge the invitation rows with the user rows
        const mergedRows = [];
        invitationRows.forEach((invite) => {
            const matchedUserIndex = restructuredUserRows.findIndex(
                (user) => invite.email === user.user.email
            );
            if (matchedUserIndex >= 0) {
                restructuredUserRows[matchedUserIndex] = {
                    ...restructuredUserRows[matchedUserIndex],
                    invite
                };
            } else {
                mergedRows.push({
                    invite,
                    user: null
                });
            }
        });

        mergedRows.push(...restructuredUserRows);

        // Filter out rows with an accepted invite, but doesn't have a user (probably b/c the user was disabled)
        // When the user toggles to include disabled users, the user and their invite will be shown.
        const filteredRows = mergedRows.filter((row) => {
            const { invite, user } = row;

            if (!user && !isInvitePending(invite)) {
                return false;
            }

            return true;
        });

        if (!this.unmount) {
            this.setState({
                combinedUserRows: filteredRows
            });
        }
    };

    renderInvitationStatus = (row) => {
        const { t } = this.props;
        const { invite } = row;

        if (!invite) {
            return '-';
        }

        return (
            <span>
                {isInvitePending(invite) ? (
                    <Chip
                        icon={
                            <WatchLater
                                sx={{
                                    '&.MuiChip-icon': {
                                        color: 'userInvitationStatus.pending.icon'
                                    },
                                    color: 'userInvitationStatus.pending.icon'
                                }}
                            />
                        }
                        sx={{
                            backgroundColor:
                                'userInvitationStatus.pending.background',
                            border: '1px solid',
                            borderColor:
                                'userInvitationStatus.pending.borderColor',
                            color: 'userInvitationStatus.pending.text'
                        }}
                        size="small"
                        label={t('Users.pending')}
                    />
                ) : (
                    <Chip
                        icon={
                            <Check
                                sx={{
                                    '&.MuiChip-icon': {
                                        color: 'userInvitationStatus.accepted.icon'
                                    },
                                    color: 'userInvitationStatus.accepted.icon'
                                }}
                            />
                        }
                        sx={{
                            backgroundColor:
                                'userInvitationStatus.accepted.background',
                            border: '1px solid',
                            borderColor:
                                'userInvitationStatus.accepted.borderColor',
                            color: 'userInvitationStatus.accepted.text'
                        }}
                        size="small"
                        label={t('Users.accepted')}
                    />
                )}
            </span>
        );
    };

    handleResendInvitation = (invite) => {
        const { invitationCode, platformTenantId, email } = invite;

        this.setState({
            resending: { email, invitationCode, platformTenantId }
        });
    };

    handleRevokeInvitation = (invite) => {
        const { email, id } = invite;

        this.setState({
            revoking: { email, invitationId: id }
        });
    };

    handleToggleChange = (p) => {
        const { key, checked } = p;

        const { currToggles } = this.state;
        const toggle = {
            ...currToggles[key],
            enabled: checked
        };
        currToggles[key] = toggle;
        this.setState({ currToggles });
        this.rerender();
    };

    revokeInvitation = async (invitationId) => {
        const { t } = this.props;

        try {
            await axios.delete(`/invitation/${invitationId}`);

            Notify.s(t('Users.revoked_user_successfully'));
            this.setState({ revoking: false });
            this.rerender();
        } catch (e) {
            Notify.e(t('Common.error'));
        }
    };

    resendInvitation = async (email) => {
        const { match, session, t } = this.props;
        const { clientId } = match.params;

        try {
            await this.WelcomeEmail.sendWelcomeEmail({
                clientId,
                emails: [email],
                session,
                t
            });
            Notify.s(t('Users.resent_invitation', { email }));
        } catch (e) {
            Notify.e(e);
        }
    };

    initToggles() {
        const { t } = this.props;

        const currState = state();
        const inputToggles = [
            {
                defaultEnabled: true,
                key: 'excludeDisabled',
                parameter: { field: 'excludeDisabled', value: true },
                toggleText: `${t('Users.exclude_disabled')}`,
                tooltip: 'Users.exclude_disabled'
            }
        ];

        const currToggles = {};

        if (inputToggles && inputToggles.length) {
            inputToggles.forEach((toggle) => {
                const { key, defaultEnabled } = toggle;
                currToggles[key] = { ...toggle, enabled: defaultEnabled };
            });
        }

        currState.inputToggles = inputToggles;
        currState.currToggles = currToggles;
        return currState;
    }

    renderActions = (row) => {
        const { t } = this.props;
        const { invite, user } = row;

        // If there's an invite and no user AND the invite is pending,
        //   => show the resend and revoke buttons
        if (!user && invite && isInvitePending(invite)) {
            return (
                <>
                    <DarkTooltip arrow title={t('Tooltips.resend_invitation')}>
                        <Button
                            onClick={() => this.handleResendInvitation(invite)}
                            size="icon"
                            variant="iconWithoutBorder"
                        >
                            <IconWithVariant variant="button" src="SendEmail" />
                        </Button>
                    </DarkTooltip>
                    <DarkTooltip arrow title={t('Tooltips.revoke_invitation')}>
                        <Button
                            onClick={() => this.handleRevokeInvitation(invite)}
                            size="icon"
                            variant="iconWithoutBorder"
                        >
                            <IconWithVariant
                                variant="danger"
                                src="CloseCircle"
                            />
                        </Button>
                    </DarkTooltip>
                </>
            );
        }

        const { id, disabled, email, name } = user;

        // if there's a user
        //   => show enable/disable buttons
        if (disabled) {
            return (
                <DarkTooltip arrow title={t('Tooltips.enableUser')}>
                    <Button
                        onClick={() =>
                            this.setState({ enabling: { email, id, name } })
                        }
                        size="icon"
                        variant="iconWithoutBorder"
                    >
                        <IconWithVariant variant="success" src="ValidUser" />
                    </Button>
                </DarkTooltip>
            );
        }

        return (
            <DarkTooltip arrow title={t('Tooltips.disableUser')}>
                <Flex justifySelf="flex-end" width="fit-content">
                    <DisableButton
                        email={email}
                        onClick={() =>
                            this.setState({ disabling: { email, id, name } })
                        }
                    />
                </Flex>
            </DarkTooltip>
        );
    };

    renderRow = (row) => {
        const { permissions } = this.state;
        const { invite, user } = row;
        const email = user?.email || invite?.email;

        const userData = user || {
            email: invite.email,
            firstName: invite.email,
            lastName: '',
            name: invite.email
        };

        return (
            <>
                <TableCell>
                    <User data={userData}>
                        <UserIconAndName includeTitle />
                    </User>
                </TableCell>
                <TableCell
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                    <span>{email}</span>
                </TableCell>
                <TableCell style={{ minWidth: '200px' }}>
                    <EditUserOrInvitePermission
                        permissions={permissions}
                        onSuccessFn={this.rerender}
                        user={row.user}
                        invite={row.invite}
                    />
                </TableCell>
                <TableCell style={{ minWidth: '100px' }}>
                    {this.renderInvitationStatus(row)}
                </TableCell>
                <TableCell align="right" style={{ minWidth: '150px' }}>
                    {this.renderActions(row)}
                </TableCell>
            </>
        );
    };

    renderTable = () => {
        const { t } = this.props;
        const { rerender, combinedUserRows, inputToggles, currToggles } =
            this.state;

        const footer = inputToggles.map((toggle) => {
            const { key, tooltip, toggleText } = toggle;
            return (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={
                                !!currToggles[key] && currToggles[key].enabled
                            }
                            color="primary"
                            onChange={(e) =>
                                this.handleToggleChange({
                                    checked: e.target.checked,
                                    key
                                })
                            }
                        />
                    }
                    key={`table_toggle_${key}`}
                    label={
                        <Box display="inline-flex" alignItems="center">
                            {toggleText}
                            {tooltip && (
                                <TooltipQ text={t(`Tooltips.${tooltip}`)} />
                            )}
                        </Box>
                    }
                />
            );
        });

        return (
            <ManagedTable
                footer={footer}
                handleRows={this.handleRows}
                headers={headers}
                renderBody={this.renderRow}
                renderBodyKey="id"
                renderFooter
                rerender={rerender}
                rows={combinedUserRows}
                rowsPerPage={10}
                searchEnabled
                searchFields={searchFields}
                sortDirection="ASC"
                sortFieldType="string"
                sortKey="firstName"
            />
        );
    };

    renderConfirmDisable = () => {
        const { t } = this.props;
        const { disabling } = this.state;

        if (!disabling) {
            return null;
        }

        return (
            <Confirm
                modalOpen={!!disabling}
                promptConfirmFn={() => this.disable({ id: disabling.id })}
                promptCancelFn={() => {
                    this.setState({ disabling: false });
                }}
                promptTitle={t('Users.confirm_disable.title')}
                promptText={
                    <Trans
                        i18nKey="Users.confirm_disable.text.manageUsers"
                        values={{
                            email: disabling.email,
                            name: disabling.name
                        }}
                        components={{
                            bold: <span style={{ fontWeight: 600 }} />,
                            br: <br />
                        }}
                    />
                }
            />
        );
    };

    renderConfirmEnable = () => {
        const { t } = this.props;
        const { enabling } = this.state;

        if (!enabling) {
            return null;
        }

        return (
            <Confirm
                modalOpen={!!enabling}
                promptConfirmFn={() => this.enable({ id: enabling.id })}
                promptCancelFn={() => {
                    this.setState({ enabling: false });
                }}
                promptTitle={t('Users.confirm_enable.title')}
                promptText={
                    <Trans
                        i18nKey="Users.confirm_enable.text.manageUsers"
                        values={{
                            email: enabling.email,
                            name: enabling.name
                        }}
                        components={{
                            bold: <span style={{ fontWeight: 600 }} />,
                            br: <br />
                        }}
                    />
                }
            />
        );
    };

    renderConfirmRevoke = () => {
        const { t } = this.props;
        const { revoking } = this.state;

        if (!revoking) {
            return null;
        }

        const { email, invitationId } = revoking;

        return (
            <Confirm
                modalOpen={!!revoking}
                promptConfirmFn={() => this.revokeInvitation(invitationId)}
                promptCancelFn={() => {
                    this.setState({ revoking: false });
                }}
                promptTitle={t('Users.confirm_revoke.title')}
                promptText={
                    <Typography
                        variant="span"
                        dangerouslySetInnerHTML={{
                            __html: t('Users.confirm_revoke.text', { email })
                        }}
                    />
                }
            />
        );
    };

    renderConfirmResend = () => {
        const { t } = this.props;
        const { resending } = this.state;
        const { email } = resending;

        if (!resending) {
            return null;
        }

        return (
            <Confirm
                modalOpen={!!resending}
                promptConfirmFn={() => this.resendInvitation(email)}
                promptCancelFn={() => {
                    this.setState({ resending: false });
                }}
                setHTMLDangerously
                promptTitle={t('Users.confirm_resend.title')}
                promptText={
                    <Typography
                        variant="span"
                        dangerouslySetInnerHTML={{
                            __html: t('Users.confirm_resend.text', { email })
                        }}
                    />
                }
            />
        );
    };

    render() {
        const { match, session, tenants } = this.props;
        const { clientId, partnerId } = match.params;
        const { loaded, disabling, enabling, revoking, resending } = this.state;

        const sessionHelper = new SessionHelper(session);
        const platformTenantId = partnerId || sessionHelper.getTenantId();

        const partnerTenantInfo = tenants[partnerId];
        const partnerTenant = partnerTenantInfo?.tenant;

        let isANM = false;
        // If we're viewing a client, it will never be have an identityTenantType of ANM.
        // However, if we're viewing a partner, then we should get the ANM tenant status from the viewed partner tenant ideally.
        // But, if it's not avilable for some reason (which I don't think would ever happen), then get it from the session's tenant as a fallback.
        if (!clientId) {
            isANM = partnerTenant
                ? partnerTenant.identityTenantType === 'ANM'
                : sessionHelper.isANM();
        }

        // This is fine to always be from the session.
        const userId = sessionHelper.getUserId();

        return (
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Paper
                        elevation={0}
                        variant="outlined"
                        data-trackid="sharedPagesManageUsersPageContainer"
                    >
                        {disabling && this.renderConfirmDisable()}
                        {enabling && this.renderConfirmEnable()}
                        {!!revoking && this.renderConfirmRevoke()}
                        {!!resending && this.renderConfirmResend()}

                        <Box display="flex" justifyContent="flex-end">
                            <Box mt="8px">
                                <AddUser
                                    data-trackid="addNewUser_partner"
                                    rerender={this.rerender}
                                    clientId={clientId}
                                    platformTenantId={platformTenantId}
                                    userId={userId}
                                    isANM={isANM}
                                />
                            </Box>
                        </Box>

                        <Grid item xs={12}>
                            {loaded && this.renderTable()}
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        );
    }
}

export default withRouter(withTranslation()(Users));
